const API_HOST = (
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000"
).replace(/\/api\/v1\/?$/, "");

export const API_BASE_URL = `${API_HOST.replace(/\/$/, "")}/api/v1`;
export const API_ORIGIN = API_HOST.replace(/\/$/, "");
export const SOCKET_URL =
  process.env.REACT_APP_SOCKET_URL || API_HOST.replace(/\/$/, "");

const TOKEN_STORAGE_KEYS = ["accessToken", "authToken", "token"];
const REFRESH_TOKEN_STORAGE_KEY = "refreshToken";
const AUTH_STORAGE_KEYS = [
  "isLoggedIn",
  ...TOKEN_STORAGE_KEYS,
  REFRESH_TOKEN_STORAGE_KEY,
  "authUser",
  "userRole",
];
let refreshRequest;

const emitToast = (type, message) => {
  if (!message || typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("admin-toast", { detail: { type, message } }),
  );
};

const getResponseMessage = (body, fallback) => {
  if (typeof body === "string") return body || fallback;

  return body?.message || body?.data?.message || fallback;
};

export const clearStoredAuthSession = () => {
  AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
};

export const storeAuthTokens = ({ accessToken, refreshToken }) => {
  if (!accessToken || !refreshToken) {
    throw new Error("Authentication response did not include both access and refresh tokens.");
  }

  // Keep authToken in sync for older parts of the admin client while using
  // accessToken as the canonical token for every authenticated request.
  localStorage.setItem("accessToken", accessToken);
  localStorage.setItem("authToken", accessToken);
  localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
};

export const isJwtExpired = (token) => {
  if (!token) return true;

  const [, payload] = token.split(".");

  if (!payload) return false;

  try {
    const normalizedPayload = payload
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(payload.length / 4) * 4, "=");
    const decodedPayload = JSON.parse(atob(normalizedPayload));
    const expiry = decodedPayload?.exp;

    return typeof expiry === "number" && expiry * 1000 <= Date.now();
  } catch (error) {
    return false;
  }
};

const redirectToLogin = () => {
  if (window.location.pathname !== "/login") {
    window.location.replace("/login");
  }
};

const logoutExpiredSession = () => {
  clearStoredAuthSession();
  redirectToLogin();
};

export const getAuthToken = () => {
  return getStoredAuthToken() || process.env.REACT_APP_API_TOKEN || "";
};

export const getStoredAuthToken = () =>
  TOKEN_STORAGE_KEYS.map((key) => localStorage.getItem(key)).find(Boolean) || "";

export const getStoredRefreshToken = () =>
  localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY) || "";

const buildHeaders = (headers = {}, { skipAuthRedirect = false } = {}) => {
  const token = skipAuthRedirect ? getStoredAuthToken() : getAuthToken();

  return {
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const parseResponse = async (response) => {
  const contentType = response.headers.get("content-type") || "";
  const body = contentType.includes("application/json")
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    const message =
      typeof body === "object" && body !== null
        ? body.message || body.error
        : body;

    const error = new Error(message || "Something went wrong. Please try again.");
    error.status = response.status;
    throw error;
  }

  return body;
};

const refreshAccessToken = () => {
  if (refreshRequest) return refreshRequest;

  refreshRequest = (async () => {
    const refreshToken = getStoredRefreshToken();
    if (!refreshToken) throw new Error("Your session has expired. Please sign in again.");

    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });
    const body = await parseResponse(response);
    const payload = body?.data ?? body ?? {};
    const accessToken = payload.accessToken || payload.access_token || payload.token;
    const nextRefreshToken = payload.refreshToken || payload.refresh_token;

    storeAuthTokens({ accessToken, refreshToken: nextRefreshToken });
    return accessToken;
  })().finally(() => {
    refreshRequest = undefined;
  });

  return refreshRequest;
};

export const apiRequest = async (path, options = {}) => {
  const { headers, skipAuthRedirect = false, toast = true, ...requestOptions } = options;
  const method = String(requestOptions.method || "GET").toUpperCase();
  const shouldToast = toast && !skipAuthRedirect && method !== "GET";
  const sendRequest = () => fetch(`${API_BASE_URL}${path}`, {
    ...requestOptions,
    headers: buildHeaders(headers, { skipAuthRedirect }),
  });

  try {
    let response = await sendRequest();

    if (response.status === 401 && skipAuthRedirect) {
      const body = await ((response.headers.get("content-type") || "").includes("application/json") ? response.json() : response.text());
      const message = typeof body === "object" && body !== null ? body.message || body.error : body;
      const error = new Error(message || "Authentication failed. Please check your credentials and try again.");
      error.status = 401;
      throw error;
    }

    if (response.status === 401) {
      try {
        await refreshAccessToken();
        response = await sendRequest();
      } catch (refreshError) {
        logoutExpiredSession();
        refreshError.status = refreshError.status || 401;
        throw refreshError;
      }

      if (response.status === 401) {
        logoutExpiredSession();
      }
    }

    const body = await parseResponse(response);
    if (shouldToast) emitToast("success", getResponseMessage(body, "Saved successfully."));
    return body;
  } catch (error) {
    if (shouldToast && error?.status !== 401) {
      emitToast("error", error.message || "Unable to complete the request.");
    }
    throw error;
  }
};
